import {
  REMINDER_TRIGGER_TYPES,
  REMINDER_TYPES,
  type TReminder,
} from '@src/shared/types/reminder';
import { dayjsUtils } from '@src/shared/utils/helpers/dayjs';

import {
  getCurrentScheduleFields,
  getUserTimezone,
} from './get-current-schedule-fields';
import {
  createReminderInitialValues,
  createStageReminderInitialValues,
  type TAudienceGroup,
  type TCreateReminderFormValues,
} from '../components/create-reminder/schema';

function parseVariables(
  variables: TReminder['variables']
): Record<string, string> {
  if (!variables) return {};
  if (typeof variables === 'object') {
    return variables as Record<string, string>;
  }
  try {
    const parsed = JSON.parse(variables) as unknown;
    return typeof parsed === 'object' &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function audienceGroupFromReminder(reminder: TReminder): TAudienceGroup {
  return reminder.type === REMINDER_TYPES.bulkLead ? 'leads' : 'program';
}

function getScheduleFieldsFromReminder(reminder: TReminder) {
  const timezone = reminder.timezone || getUserTimezone();

  if (reminder.date) {
    const zoned = dayjsUtils(reminder.date).tz(timezone);
    if (zoned.isValid()) {
      return {
        scheduledDate: zoned.format('YYYY-MM-DD'),
        scheduledTime: zoned.format('HH:mm'),
        timezone,
      };
    }
  }

  return getCurrentScheduleFields();
}

function getQuestionId(reminder: TReminder) {
  return String(
    typeof reminder.question === 'object' &&
      reminder.question !== null &&
      'id' in reminder.question
      ? (reminder.question as { id: number }).id
      : (reminder.question ?? '')
  );
}

function getStageIds(reminder: TReminder) {
  return (
    reminder.stages?.map((stage) => stage.id) ??
    (reminder.stage?.id ? [reminder.stage.id] : [])
  );
}

export function editStageReminderInitialValuesFromReminder(
  reminder: TReminder
): TCreateReminderFormValues {
  const base = createStageReminderInitialValues();
  const scheduleFields = getScheduleFieldsFromReminder(reminder);
  const isSpecificDate = reminder.type === REMINDER_TYPES.specificDate;

  return {
    ...base,
    name: reminder.name ?? '',
    content: reminder.content ?? '',
    subject: reminder.subject ?? '',
    notification_channel: reminder.notification_channel,
    template: reminder.template ?? base.template,
    attachments: reminder.attachments ?? [],
    variables: parseVariables(reminder.variables),
    type: reminder.type,
    trigger_type: reminder.trigger_type || REMINDER_TRIGGER_TYPES.before,
    rule: String(reminder.rule?.id || ''),
    stages: getStageIds(reminder),
    question: getQuestionId(reminder),
    is_rule_achieved: reminder.is_rule_achieved ? 'ACHIEVED' : 'NOT_ACHIEVED',
    rule_choices: reminder.rule_choices ?? [],
    rule_type: reminder.rule?.type ?? '',
    frequency: reminder.frequency ?? 0,
    frequency_count: reminder.frequency_count ?? 0,
    repeatSending:
      reminder.type === REMINDER_TYPES.otherRules &&
      Boolean(reminder.frequency || reminder.frequency_count),
    scheduleDelivery: isSpecificDate,
    ...(isSpecificDate ? scheduleFields : {}),
    date: reminder.date ?? '',
    flight_type: reminder.flight_type ?? '',
  };
}

export function duplicateReminderInitialValuesFromReminder(
  reminder: TReminder
): TCreateReminderFormValues {
  const base = createReminderInitialValues();
  const scheduleFields = getScheduleFieldsFromReminder(reminder);

  return {
    ...base,
    name: reminder.name ? `${reminder.name} (copy)` : '',
    content: reminder.content ?? '',
    subject: reminder.subject ?? '',
    notification_channel: reminder.notification_channel,
    view: reminder.view ?? base.view,
    template: reminder.template ?? base.template,
    attachments: reminder.attachments ?? [],
    variables: parseVariables(reminder.variables),
    audienceGroup: audienceGroupFromReminder(reminder),
    program: reminder.program ?? base.program,
    scheduleDelivery: true,
    ...scheduleFields,
  };
}
